import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { type OptionInput } from '../../../../api/types';
import ArrayField from '../../../../components/fields/ArrayField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { OptionTypeFieldRenderer } from './OptionTypeField';
import { type OptionBlockFormValues, getDefaultOptionTypeInput } from './utils';

const OptionTypeSingleField = ({ name, disabled = false }: { name: string; disabled?: boolean }) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { configurator } = useSystemOptions();

    return (
        <ArrayField<OptionInput, OptionBlockFormValues>
            label="Options"
            name={name}
            render={({ remove, push }, options) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {options?.map((option, index) => (
                        <Card
                            key={option.id ?? index.toString()}
                            extra={
                                !disabled && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(index)}
                                        type="link"
                                        danger
                                    />
                                )
                            }
                            title={t('variantConfiguratorDetails:sections.options.blockTypeSetting', {
                                number: index + 1,
                            })}
                            type="inner"
                        >
                            <Row gutter={14}>
                                <Col lg={8} xs={24}>
                                    <FormFields.TranslatedInputField
                                        {...t('variantConfiguratorDetails:fields.options.optionName', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]label`}
                                        required
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <FormFields.SelectField
                                        {...t('variantConfiguratorDetails:fields.options.optionType', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]type`}
                                        options={configurator.selectOptionTypes}
                                        required
                                        showSearch
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <OptionTypeFieldRenderer
                                        disabled={disabled}
                                        prefix={`${name}[${index}]`}
                                        reference={`${name}[${index}]type`}
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <SingleUploadField
                                        {...t('variantConfiguratorDetails:fields.options.image', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        extensions={allowedExtensions.image}
                                        listType="picture-card"
                                        name={`${name}[${index}]image`}
                                        size="small"
                                        style={{
                                            marginBottom: 0,
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    ))}
                    {!disabled && (
                        <Button icon={<PlusOutlined />} onClick={() => push(getDefaultOptionTypeInput())} type="link">
                            {t('variantConfiguratorDetails:actions.options.addSelectSetting')}
                        </Button>
                    )}
                </Space>
            )}
        />
    );
};

export default OptionTypeSingleField;
